/**
 * EquipmentSourceSelector.js - P0-CORE: Selector de fonte de equipamento
 * 
 * Suporta:
 * - Garantia: Busca de vendas e seleção de item vendido
 * - Stock para Reparação: Lista de items do inventário para reparar
 * - Equipamento Interno: Lista de ativos internos da loja
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from 'sonner';
import {
  Search, Receipt, Store, Settings, Package, Check, ChevronsUpDown, 
  FileText, Calendar, Phone, User, AlertTriangle, Smartphone
} from 'lucide-react';
import { formatCurrency, formatDisplayCode, cn } from '@/lib/utils';
import { servicesAPI } from '@/lib/api';

// ============================================================================
// WARRANTY SALE SEARCH - P0: Buscar vendas para ligar a reclamação de garantia
// ============================================================================
export function WarrantySaleSearch({ onSaleSelected, selectedSaleId = null, locationId = null }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Search sales
  const handleSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await servicesAPI.searchSalesForWarranty(searchTerm, locationId);
      setResults(response.data?.sales || []);
    } catch (error) {
      console.error('Warranty search error:', error);
      if (error?.response?.status !== 400) {
        toast.error('Erro ao pesquisar vendas');
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, handleSearch]);

  const handleSelectSale = (sale) => {
    setSelectedSale(sale);
    setSelectedItem(null);
    // If only one item, auto-select it
    if (sale.items?.length === 1) {
      handleSelectItem(sale.items[0], sale);
    }
  };

  const handleSelectItem = (item, sale) => {
    setSelectedItem(item);
    setOpen(false);
    
    onSaleSelected?.({
      sale_id: sale.sale_id || sale.id,
      sale_number: sale.fiscal_document_number || sale.sale_number || sale.document_number || sale.invoice_number,
      sale_date: sale.sale_date || sale.created_at,
      customer_name: sale.customer_name,
      customer_phone: sale.customer_phone,
      client_id: sale.client_id,
      location_id: sale.location_id || locationId || null,
      sale_item_id: item.item_id || item.id,
      sale_item_index: item.item_index ?? 0,
      product_name: item.product_name || item.name,
      product_brand: item.product_brand || item.brand,
      product_model: item.product_model || item.model,
      product_category: item.category || item.product_category || item.category_name || null,
      product_subcategory: item.subcategory || item.product_subcategory || item.subcategory_name || null,
      serial_number: item.serial_number || item.imei1,
      warranty_months: item.warranty_months || 12,
      specs: item.specs || {}
    });
  };

  return (
    <div className="space-y-3" data-testid="warranty-sale-search">
      <Label className="flex items-center gap-2">
        <Receipt className="h-4 w-4 text-green-600" />
        Buscar Venda para Garantia
      </Label>
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            data-testid="warranty-search-trigger"
          >
            {selectedSale ? (
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {formatDisplayCode(selectedSale.fiscal_document_number || selectedSale.sale_number || selectedSale.document_number || selectedSale.invoice_number, 'FAC')} - {selectedSale.customer_name}
              </span>
            ) : (
              <span className="text-muted-foreground">Pesquisar por nº documento, telefone, IMEI, serial...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder="Pesquisar vendas..." 
              value={search}
              onValueChange={setSearch}
              data-testid="warranty-search-input"
            />
            <CommandList>
              {loading && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  A pesquisar...
                </div>
              )}
              {!loading && results.length === 0 && search.length >= 3 && (
                <CommandEmpty>Nenhuma venda encontrada</CommandEmpty>
              )}
              {!loading && results.length > 0 && (
                <CommandGroup heading="Vendas Encontradas">
                  {results.map((sale) => (
                    <CommandItem
                      key={sale.sale_id || sale.id}
                      value={sale.sale_id || sale.id}
                      onSelect={() => handleSelectSale(sale)}
                      className="flex flex-col items-start p-2"
                    >
                      <div className="flex justify-between w-full">
                        <span className="font-medium">{formatDisplayCode(sale.fiscal_document_number || sale.sale_number || sale.document_number || sale.invoice_number, 'FAC')}</span>
                        <Badge variant="outline" className="text-xs">
                          {(sale.sale_date || sale.created_at) ? new Date(sale.sale_date || sale.created_at).toLocaleDateString('pt-PT') : 'N/A'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <User className="h-3 w-3" />
                        {sale.customer_name}
                        <Phone className="h-3 w-3 ml-2" />
                        {sale.customer_phone}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {sale.items?.length || 0} item(s) - Total: {formatCurrency(sale.total)}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Item Selection */}
      {selectedSale && selectedSale.items?.length > 1 && (
        <div className="space-y-2">
          <Label className="text-xs">Selecionar Item da Venda</Label>
          <div className="grid gap-2">
            {selectedSale.items.map((item, idx) => (
              <div
                key={item.id || idx}
                className={cn(
                  "border rounded-md p-2 cursor-pointer transition-colors",
                  selectedItem?.id === item.id 
                    ? "border-green-500 bg-green-50" 
                    : "hover:border-slate-300"
                )}
                onClick={() => handleSelectItem(item, selectedSale)}
                data-testid={`warranty-item-${idx}`}
              >
                <div className="flex justify-between">
                  <span className="font-medium text-sm">{item.product_name || item.name}</span>
                  {selectedItem?.id === item.id && <Check className="h-4 w-4 text-green-600" />}
                </div>
                <div className="text-xs text-muted-foreground">
                  {item.serial_number || item.imei1 || 'S/N não registado'}
                  {item.warranty_months && ` • Garantia: ${item.warranty_months} meses`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Item Summary */}
      {selectedItem && (
        <div className="bg-green-100 border border-green-300 rounded-lg p-3">
          <div className="flex items-center gap-2 text-green-800 font-medium text-sm">
            <Check className="h-4 w-4" />
            Item selecionado para garantia
          </div>
          <div className="text-xs text-green-700 mt-1">
            {selectedItem.product_name} - {selectedItem.serial_number || selectedItem.imei1}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// REPAIR STOCK SELECTOR - P0-STOCK-REPAIR-01: Lista itens do stock para reparação
// ============================================================================
export function RepairStockSelector({ locationId, preferredLocationId, onItemSelected, selectedItemId = null }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState(null);

  // P0-STOCK-REPAIR-01: Usar preferredLocationId (não locationId estrito)
  const effectiveLocationId = preferredLocationId || locationId || null;

  // Load repair stock items - cross-location (no strict filter)
  const loadItems = useCallback(async (searchTerm = '') => {
    setLoading(true);
    setError(null);
    try {
      // Passar preferredLocationId para ordenação (itens da loja preferida aparecem primeiro)
      const response = await servicesAPI.getRepairStock(effectiveLocationId, searchTerm);
      setItems(response.data?.items || []);
    } catch (err) {
      console.error('Repair stock error:', err);
      setError('Erro ao carregar stock para reparação');
      toast.error('Erro ao carregar stock para reparação');
    } finally {
      setLoading(false);
    }
  }, [effectiveLocationId]);

  // Initial load
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      loadItems(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, loadItems]);

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setOpen(false);
    
    onItemSelected?.({
      inventory_item_id: item.inventory_item_id,
      product_id: item.product_id,
      product_name: item.name,
      product_brand: item.brand,
      product_model: item.model,
      serial_number: item.serial_number || item.imei,
      category_id: item.category_id,
      category_name: item.category_name,
      specs: item.specs || {},
      specs_final: item.specs_final || item.specs || {},
      specs_template: item.specs_template || {},
      missing_required_specs: item.missing_required_specs || [],
      condition: item.condition,
      location_id: item.location_id,
      location_name: item.location_name,
    });
  };

  return (
    <div className="space-y-3" data-testid="repair-stock-selector">
      <Label className="flex items-center gap-2">
        <Store className="h-4 w-4 text-amber-600" />
        Stock para Reparação
        {items.length > 0 && (
          <Badge variant="outline" className="text-xs bg-amber-100">{items.length} disponíveis</Badge>
        )}
      </Label>
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            data-testid="repair-stock-trigger"
          >
            {selectedItem ? (
              <span className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                {selectedItem.name} {selectedItem.serial_number || selectedItem.imei ? `- ${selectedItem.serial_number || selectedItem.imei}` : ''}
              </span>
            ) : (
              <span className="text-muted-foreground">Selecionar item do stock para reparar...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[520px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder="Pesquisar por nome, SKU, serial, IMEI..." 
              value={search}
              onValueChange={setSearch}
              data-testid="repair-stock-search-input"
            />
            <CommandList>
              {loading && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  A carregar...
                </div>
              )}
              {error && (
                <div className="p-4 text-center text-sm text-red-500">
                  {error}
                </div>
              )}
              {!loading && !error && items.length === 0 && (
                <CommandEmpty>
                  <div className="flex flex-col items-center gap-2 py-4">
                    <AlertTriangle className="h-8 w-8 text-amber-500" />
                    <span className="font-medium">Nenhum item para reparação encontrado</span>
                    <span className="text-xs text-muted-foreground text-center">
                      Marque itens no Inventário com condição &quot;Defeituoso&quot;, &quot;Razoável&quot;<br/>
                      ou estado &quot;Em Reparação&quot; para aparecerem aqui.
                    </span>
                  </div>
                </CommandEmpty>
              )}
              {!loading && !error && items.length > 0 && (
                <CommandGroup heading={`${items.length} Itens Disponíveis`}>
                  {items.map((item) => (
                    <CommandItem
                      key={item.inventory_item_id}
                      value={item.inventory_item_id}
                      onSelect={() => handleSelectItem(item)}
                      className="flex flex-col items-start p-2"
                      data-testid={`repair-stock-item-${item.inventory_item_id}`}
                    >
                      <div className="flex justify-between w-full">
                        <span className="font-medium">{item.name}</span>
                        <div className="flex gap-1">
                          {item.is_preferred_location && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Esta loja</Badge>
                          )}
                          <Badge variant="outline" className={cn(
                            "text-xs",
                            item.condition === 'defective' && "bg-red-100 text-red-800",
                            item.condition === 'fair' && "bg-yellow-100 text-yellow-800",
                            item.condition === 'for_parts' && "bg-orange-100 text-orange-800",
                          )}>
                            {item.condition === 'defective' ? 'Defeituoso' : 
                             item.condition === 'fair' ? 'Razoável' : 
                             item.condition === 'for_parts' ? 'Para Peças' : item.condition}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <span>{item.sku}</span>
                        {item.brand && <><span>•</span><span>{item.brand} {item.model}</span></>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        SN/IMEI: {item.serial_number || item.imei || 'N/A'}
                        {item.location_name && ` • ${item.location_name}`}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Item with Specs */}
      {selectedItem && (
        <Card className="border-amber-200 bg-amber-50" data-testid="repair-stock-selected-item">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              Item Selecionado
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><strong>Nome:</strong> {selectedItem.name}</div>
              <div><strong>SKU:</strong> {selectedItem.sku}</div>
              <div><strong>Marca:</strong> {selectedItem.brand || 'N/A'}</div>
              <div><strong>Modelo:</strong> {selectedItem.model || 'N/A'}</div>
              <div><strong>Serial/IMEI:</strong> {selectedItem.serial_number || selectedItem.imei || 'N/A'}</div>
              <div><strong>Condição:</strong> {selectedItem.condition}</div>
              {selectedItem.location_name && (
                <div className="col-span-2"><strong>Localização:</strong> {selectedItem.location_name}</div>
              )}
            </div>
            
            {/* Specs Display (specs_final = merge completo) */}
            {Object.keys(selectedItem.specs_final || {}).length > 0 && (
              <div className="border-t pt-2 mt-2" data-testid="repair-stock-specs">
                <strong className="text-xs">Especificações:</strong>
                <div className="grid grid-cols-2 gap-1 mt-1 text-xs text-muted-foreground">
                  {Object.entries(selectedItem.specs_final).map(([key, value]) => (
                    <div key={key}>
                      <span className="capitalize">{key.replace(/_/g, ' ')}:</span> {String(value)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(selectedItem.specs_final || {}).length === 0 && (
              <div className="border-t pt-2 mt-2 text-xs text-muted-foreground" data-testid="repair-stock-specs-empty">
                Sem especificações registadas
              </div>
            )}
            
            {/* Aviso de specs em falta (não bloqueia) */}
            {(selectedItem.missing_required_specs || []).length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2" data-testid="repair-stock-missing-specs-warning">
                <div className="flex items-center gap-1 text-xs text-yellow-800">
                  <AlertTriangle className="h-3 w-3" />
                  <strong>Specs em falta:</strong> {selectedItem.missing_required_specs.join(', ')}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ============================================================================
// INTERNAL ASSET SELECTOR - P0: Lista ativos internos da loja
// ============================================================================
export function InternalAssetSelector({ locationId, onAssetSelected, selectedAssetId = null }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Load internal assets
  const loadAssets = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const response = await servicesAPI.getInternalAssets(locationId, searchTerm);
      setAssets(response.data?.assets || []);
    } catch (error) {
      console.error('Internal assets error:', error);
      toast.error('Erro ao carregar ativos internos');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  // Initial load
  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      loadAssets(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, loadAssets]);

  const handleSelectAsset = (asset) => {
    setSelectedAsset(asset);
    setOpen(false);
    
    onAssetSelected?.({
      internal_asset_id: asset.id,
      asset_tag: asset.asset_tag,
      asset_name: asset.name,
      asset_brand: asset.brand,
      asset_model: asset.model,
      serial_number: asset.serial_number,
      category: asset.category,
      specs: asset.specs || {},
      location_id: asset.location_id
    });
  };

  return (
    <div className="space-y-3" data-testid="internal-asset-selector">
      <Label className="flex items-center gap-2">
        <Settings className="h-4 w-4 text-purple-600" />
        Equipamento Interno da Loja
      </Label>
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            data-testid="internal-asset-trigger"
          >
            {selectedAsset ? (
              <span className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                [{selectedAsset.asset_tag}] {selectedAsset.name}
              </span>
            ) : (
              <span className="text-muted-foreground">Selecionar equipamento interno...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder="Pesquisar por tag, nome, serial..." 
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {loading && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  A carregar...
                </div>
              )}
              {!loading && assets.length === 0 && (
                <CommandEmpty>
                  <div className="flex flex-col items-center gap-2 py-4">
                    <AlertTriangle className="h-8 w-8 text-purple-500" />
                    <span>Nenhum ativo interno encontrado</span>
                    <span className="text-xs text-muted-foreground">
                      Os ativos internos podem ser registados no módulo de inventário
                    </span>
                  </div>
                </CommandEmpty>
              )}
              {!loading && assets.length > 0 && (
                <CommandGroup heading={`${assets.length} Ativos Internos`}>
                  {assets.map((asset) => (
                    <CommandItem
                      key={asset.id}
                      value={asset.id}
                      onSelect={() => handleSelectAsset(asset)}
                      className="flex flex-col items-start p-2"
                    >
                      <div className="flex justify-between w-full">
                        <span className="font-medium">[{asset.asset_tag}] {asset.name}</span>
                        <Badge variant="outline" className={cn(
                          "text-xs",
                          asset.status === 'active' && "bg-green-100 text-green-800",
                          asset.status === 'maintenance' && "bg-yellow-100 text-yellow-800"
                        )}>
                          {asset.status === 'active' ? 'Activo' : 
                           asset.status === 'maintenance' ? 'Manutenção' : asset.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <span>{asset.brand} {asset.model}</span>
                        {asset.category && <span>• {asset.category}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        SN: {asset.serial_number || 'N/A'}
                        {asset.location_name && ` • Local: ${asset.location_name}`}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Asset Summary */}
      {selectedAsset && (
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              Equipamento Selecionado
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><strong>Tag:</strong> {selectedAsset.asset_tag}</div>
              <div><strong>Nome:</strong> {selectedAsset.name}</div>
              <div><strong>Marca:</strong> {selectedAsset.brand || 'N/A'}</div>
              <div><strong>Modelo:</strong> {selectedAsset.model || 'N/A'}</div>
              <div><strong>Serial:</strong> {selectedAsset.serial_number || 'N/A'}</div>
              <div><strong>Estado:</strong> {selectedAsset.status}</div>
            </div>
            
            {/* Specs Display */}
            {Object.keys(selectedAsset.specs || {}).length > 0 && (
              <div className="border-t pt-2 mt-2">
                <strong className="text-xs">Especificações:</strong>
                <div className="grid grid-cols-2 gap-1 mt-1 text-xs text-muted-foreground">
                  {Object.entries(selectedAsset.specs).map(([key, value]) => (
                    <div key={key}>
                      <span className="capitalize">{key.replace(/_/g, ' ')}:</span> {String(value)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
